import React, { Fragment, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { arrayOf, bool, func, object } from 'prop-types';
import { cloneDeep, isEqual, lowerCase, map } from 'lodash';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/core';
import {
  AlertDialog,
  FormActionButtons,
} from '../../../components';
import {
  fetchTemplateCategoryList,
  orderTemplateCategoryList,
} from "../../../actions/templateCategory";
import {
  getIsLoading,
  getTemplateCategoryListResults,
  getTemplatesOrderUpdateResult,
} from "../../../reducers/templateCategory";
import {
  getUserDetails,
} from '../../../reducers/portalConfig';
import styles from './styles';
import ListContainer from '../../list';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  CATEGORY_LIST_ACCESS_TYPE_FILTER_DATA,
  CATEGORY_LIST_API_ACCESS_FILTER_DATA,
  CATEGORY_LIST_API_TYPE_FILTER_DATA,
  CATEGORY_LIST_EDIT_RULES_FILTER_DATA,
  CATEGORY_LIST_ENFORCEMENT_FILTER_DATA,
  CATEGORY_LIST_POSITION_FILTER_DATA,
  CATEGORY_LIST_USAGE_FILTER_DATA,
  FAIL,
  ROLE_PORTAL_ADMIN,
  SUCCESS,
} from '../../../constants';
import {
  getUserRole,
} from '../../../utils/rbac';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import { capitalCase } from 'change-case';
import { HeaderWithTooltip } from '../../../pageComponents/HeaderWithTooltip';
import { FilterBySelect, FilterByText } from '../../../pageComponents';

export const getTCEditLink = () => (
  '/admin/console/templatecategories/details'
);

export const TCList = (props) => {
  const {
    classes,
    fetchTemplateCategoryList,
    isLoading,
    orderTemplateCategoryList,
    results = [],
    updateOrderTemplatesResult,
    userContext,
  } = props;

  const [listRows, setListRows] = useState([]);
  const [notificationStatus, setNotificationStatus] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [selectFilters, setSelectFilters] = useState({
        position: 'ALL_POSITIONS',
        accessType: 'ALL_ACCESS',
        enforcement: 'ALL_ENFORCEMENTS',
        editRules: 'ALL_EDIT_RULES',
        usageRules: 'ALL_USAGE_RULES',
        apiType: 'ALL_API_TYPES',
        apiAccess: 'ALL_API_ACCESS',
  });
  const getTCListColumns = () => {
    const getEditRulesString = ({ enforceTemplateOrdering }) => {
      if(enforceTemplateOrdering) {
        return getI18nFormattedMessage('label.category.list.page.edit.rules.reorder');
      }
      return getI18nFormattedMessage('label.category.list.page.edit.rules.none');
    }
    return ([
      {
        id: 'name',
        label: getI18nFormattedMessage('label.category.list.page.categoryname'),
        minWidth: 100,
        link: getTCEditLink,
        value: ( item ) => {
          return item.name;
        },
      },{
        id: 'description',
        label: getI18nFormattedMessage('label.category.list.page.description'),
        minWidth: 100,
        isCellOverflow: true,
        value: ({ description }) =>
          (<span title={description}> {description} </span>),
      }, {
        id: 'position',
        label: (
          <HeaderWithTooltip
            classes={classes}
            label={getI18nFormattedMessage('label.category.list.page.position')}
            tooltipTitle={getI18nFormattedMessage('label.category.list.page.position.tooltip')}
          />
        ),
        value: ({ flexible }) => getI18nFormattedMessage(`label.category.list.page.position.${flexible ? 'flexible': 'fixed'}`),
      }, {
        id: 'accessType',
        label: (
          <HeaderWithTooltip
            classes={classes}
            label={getI18nFormattedMessage('label.category.list.page.orgrestricted')}
            tooltipTitle={getI18nFormattedMessage('label.category.list.page.orgrestricted.tooltip')}
          />
        ),
        value: ({ accessType } = {}) => (<span className={classes.tickMark}>{ accessType === 'ORG' ? '✓' : '' }</span>),
      }, {
        id: 'enforcement',
        label: (
          <HeaderWithTooltip
            classes={classes}
            label={getI18nFormattedMessage('label.category.list.page.enforcement')}
            tooltipTitle={getI18nFormattedMessage('label.category.list.page.enforcement.tooltip')}
          />
        ),
        value: ({ allowNonCompliantApisToBeUpdated } = {}) => (<span className={classes.tickMark}>{ allowNonCompliantApisToBeUpdated ? '' : '✓' }</span>),
      }, {
        id: 'edit-rules',
        label: (
          <HeaderWithTooltip
            classes={classes}
            label={getI18nFormattedMessage('label.category.list.page.edit.rules')}
            tooltipTitle={getI18nFormattedMessage('label.category.list.page.edit.rules.tooltip')}
          />
        ),
        value: (item) => getEditRulesString(item),
      }, {
        id: 'usage-rules',
        label: (
          <HeaderWithTooltip
            classes={classes}
            label={getI18nFormattedMessage('label.category.list.page.usage.rules')}
            tooltipTitle={getI18nFormattedMessage('label.category.list.page.usage.rules.tooltip')}
          />
        ),
        value: ({ templateUsageRestriction }) => getI18nFormattedMessage(`label.category.list.page.usage.rules.${lowerCase(templateUsageRestriction)}`),
      }, {
        id: 'api-type',
        label: getI18nFormattedMessage('label.category.list.page.api.type'),
        value: ({ criteria }) => criteria && criteria.apiType === 'ANY' ? capitalCase(criteria.apiType) : criteria.apiType,
      }, {
        id: 'api-access',
        label: getI18nFormattedMessage('label.category.list.page.api.access'),
        value: ({ criteria }) => criteria && capitalCase(criteria.apiAccess),
      },
    ]);
  };

  const intl = getI18n(useIntl());

  const [nameSearchText, setNameSearchText] = useState('');
  const [filterText, setFilterText] = useState('');
  const [isUnsavedDialogOpen, setIsUnsavedDialogOpen] = useState(false);

  useEffect(() => {
    setListRows(
      results.map(result => {
        return{
          ...result,
          draggable: !result.flexible,
          droppable: !result.flexible,
        };
      }));
  }, [results]);

  useEffect(() => {
    fetchTemplateCategoryList();
  }, []);

  useEffect(() => {
    if (updateOrderTemplatesResult === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.category.list.page.order.success'));
      fetchTemplateCategoryList();
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    } else if (updateOrderTemplatesResult === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.category.list.page.order.fail'));
    }
  }, [updateOrderTemplatesResult]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };
  const onFilterByNameChange = (value) => {
    setNameSearchText(value);
  };
  const onNameSearchTextChanged = () => {
    setFilterText(nameSearchText);
    applyFilters({ nameFilter: nameSearchText });
  };

  const onAddTemplateCategory = () => {
     window.location.href = '/admin/console/templatecategories/add';
  };

  const isAllowedToTemplateCategory = () => {
    const userRole = getUserRole(userContext);
    if (userRole === ROLE_PORTAL_ADMIN) {
      return true;
    }
    else {
      return false;
    }
  };
  
  const orderChanged = !isEqual(listRows.map(item => item.uuid), results.map(item => item.uuid))
                        && isEqual(listRows.length, results.length);

  const updateTemplatesOrderList = () => {
    const listOfCategoriesIds = map(listRows, item => item.uuid);
    orderTemplateCategoryList(listOfCategoriesIds);
  };

  const handleUnsavedDialogClose = () => {
    setIsUnsavedDialogOpen(false);
  };

  const handleUnsavedDialogSubmit = () => {
    setIsUnsavedDialogOpen(false);
    fetchTemplateCategoryList();
  };

  const handleCancel = () => {
    setIsUnsavedDialogOpen(true);
  };

  const applyFilters = ({ dropDownFilters, nameFilter }) => {
    const nameFilterText = nameFilter ? nameFilter : filterText;
    const dropDownFiltersToBeApplied = dropDownFilters ? dropDownFilters : selectFilters;
    setListRows(results.filter(
      item => {
        const qualifiedForName = !nameFilterText ||
                item.name && item.name.toUpperCase().includes(nameFilterText.toUpperCase());
        const qualifiedForPosition = !dropDownFiltersToBeApplied['position'] ||
              dropDownFiltersToBeApplied['position'] === 'ALL_POSITIONS' ||
              (item.flexible && dropDownFiltersToBeApplied['position'] === 'FLEXIBLE') ||
                  !item.flexible && (dropDownFiltersToBeApplied['position'] === 'FIXED');
        const qualifiedForEnforcement = !dropDownFiltersToBeApplied['enforcement'] ||
              dropDownFiltersToBeApplied['enforcement'] === 'ALL_ENFORCEMENTS' ||
          (!item.allowNonCompliantApisToBeUpdated && dropDownFiltersToBeApplied['enforcement'] === 'ENFORCED') ||
          (item.allowNonCompliantApisToBeUpdated && dropDownFiltersToBeApplied['enforcement'] === 'NOT_ENFORCED');
        const qualifiedForEditRules = !dropDownFiltersToBeApplied['editRules'] ||
              dropDownFiltersToBeApplied['editRules'] === 'ALL_EDIT_RULES' ||
              (item.enforceTemplateOrdering && dropDownFiltersToBeApplied['editRules'] === 'RE_ORDER') ||
              (!item.enforceTemplateOrdering && dropDownFiltersToBeApplied['editRules'] === 'NONE');
        const qualifiedForUsageRules = !dropDownFiltersToBeApplied['usageRules'] ||
              dropDownFiltersToBeApplied['usageRules'] === 'ALL_USAGE_RULES' ||
              (item.templateUsageRestriction === dropDownFiltersToBeApplied['usageRules']);
        const criteria = item && item.criteria;
        const qualifiedForApiType = !dropDownFiltersToBeApplied['apiType'] ||
              dropDownFiltersToBeApplied['apiType'] === 'ALL_API_TYPES' ||
              (criteria.apiType === dropDownFiltersToBeApplied['apiType']);
        const qualifiedForApiAccess = !dropDownFiltersToBeApplied['apiAccess'] ||
              dropDownFiltersToBeApplied['apiAccess'] === 'ALL_API_ACCESS' ||
              (criteria.apiAccess === dropDownFiltersToBeApplied['apiAccess']);
        const qualifiedForOrgRestriction = !dropDownFiltersToBeApplied['accessType'] ||
              dropDownFiltersToBeApplied['accessType'] === 'ALL_ACCESS' ||
              item.accessType === dropDownFiltersToBeApplied['accessType'];
        return qualifiedForName && qualifiedForPosition && qualifiedForEnforcement
                && qualifiedForEditRules && qualifiedForUsageRules
                && qualifiedForApiType && qualifiedForApiAccess && qualifiedForOrgRestriction;
      }).map(item => { return { ...item, draggable: !item.flexible, droppable: !item.flexible } }));
  };
  const onDragAndDrop = (dragIndex, hoverIndex) => {
    const rows = cloneDeep(listRows);
    const movingRow = rows[dragIndex];
    rows.splice(dragIndex, 1);
    rows.splice(hoverIndex, 0 , movingRow);
    setListRows(rows);
  }
  const onFilterBySelectChange = (selected, attribute) => {
    const filters = {
      ...selectFilters,
      [attribute]: selected,
    };
    setSelectFilters(filters);
    applyFilters({ dropDownFilters: filters });
  };
  return (
    <>
      <AlertDialog
        isOpen={isUnsavedDialogOpen}
        title={intl.getI18nMessage('label.dialog.unsaved.changes.title')}
        description={intl.getI18nMessage('label.category.list.page.unsaved.changes')}
        submitText={intl.getI18nMessage('label.ok.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        onClose={handleUnsavedDialogClose}
        onSubmit={handleUnsavedDialogSubmit}
        onCancel={handleUnsavedDialogClose}
        dialogId={'category-list-unsaved-dialog'}
      />
      <ListContainer
        notificationId="category-list-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        userContext={userContext}
        listPageId="templatecategory-list-page"
        isLoading={isLoading}
        pageHeaderTitle={intl.getI18nMessage('label.category.list.page.title')}
        pageHeaderDescription={intl.getI18nFormattedMessage('label.category.list.page.title.helpertext')}
        addButtonLabel={intl.getI18nMessage('label.category.add.button')}
        onAdd={(
          isAllowedToTemplateCategory() && onAddTemplateCategory
        )}
        filterAndSortContent={(
          <Fragment>
              <FilterByText
                id="category-filter-by-name"
                fieldContainerClass={classes.nameContainer}
                name={intl.getI18nMessage('label.filter')}
                value={nameSearchText}
                placeholder={intl.getI18nMessage('label.category.list.page.filter.by.name.placeholder')}
                handleChange={onFilterByNameChange}
                handleBlur={onNameSearchTextChanged}
              />
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['position']}
                handleChange={(val) => onFilterBySelectChange(val, 'position')}
                data={CATEGORY_LIST_POSITION_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['accessType']}
                handleChange={(val) => onFilterBySelectChange(val, 'accessType')}
                data={CATEGORY_LIST_ACCESS_TYPE_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
           
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['enforcement']}
                handleChange={(val) => onFilterBySelectChange(val, 'enforcement')}
                data={CATEGORY_LIST_ENFORCEMENT_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
          
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['editRules']}
                handleChange={(val) => onFilterBySelectChange(val, 'editRules')}
                data={CATEGORY_LIST_EDIT_RULES_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
           
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['usageRules']}
                handleChange={(val) => onFilterBySelectChange(val, 'usageRules')}
                data={CATEGORY_LIST_USAGE_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
           
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['apiType']}
                handleChange={(val) => onFilterBySelectChange(val, 'apiType')}
                data={CATEGORY_LIST_API_TYPE_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
         
              <FilterBySelect
                fieldContainerClass={classes.fieldContainer}
                selectFieldClass={classes.selectField}
                value={selectFilters['apiAccess']}
                handleChange={(val) => onFilterBySelectChange(val, 'apiAccess')}
                data={CATEGORY_LIST_API_ACCESS_FILTER_DATA}
                labelClass={classes.hideLabel}
              />
          
          </Fragment>
        )}
        columns={getTCListColumns(listRows.length)}
        rows={listRows}
        noResultsMessage={intl.getI18nMessage('label.category.list.filter.no.results')}
        hidePagination={true}
        allowOrdering={results.length === listRows.length}
        onDragAndDrop={onDragAndDrop}
      />
      {orderChanged && <FormActionButtons
        assistText={''}
        isContainerFooter
        isDisabled={false}
        nextText={intl.getI18nMessage('label.user.details.button.save')}
        onNextClick={updateTemplatesOrderList}
        onCancelClick={handleCancel}
        id="category-list-buttons"
      />}
    </>
  );
};

TCList.propTypes = {
  classes: object,
  fetchTemplateCategoryList: func,
  isLoading: bool,
  orderTemplateCategoryList: func,
  results: arrayOf(object),
  updateOrderTemplatesResult: func,
  userContext: object,
};

const mapStateToProps = (state) => ({
  isLoading: getIsLoading(state),
  userContext: getUserDetails(state),
  results: getTemplateCategoryListResults(state),
  updateOrderTemplatesResult: getTemplatesOrderUpdateResult(state),
});

const mapDispatchToProps = {
  fetchTemplateCategoryList,
  orderTemplateCategoryList,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(TCList);
